import classNames from 'classnames';
import type { CSSProperties, FunctionComponent } from 'react';

import type {
  ControlProps,
  OptionProps,
  PlaceholderProps,
} from '@atlaskit/select';
import Select, { components } from '@atlaskit/select';
import { forTemplate } from '@trello/legacy-i18n';
import { SearchIcon } from '@trello/nachos/icons/search';
import { navigate } from '@trello/router/navigate';
import { token } from '@trello/theme';

import styles from './SearchForm.less';

const format = forTemplate('navigation');
const SEE_ALL_RESULTS = 'SEE_ALL_RESULTS';
const NUMBER_OF_RESULTS_TO_SHOW = 4;

interface SearchResultOption {
  label: string;
  value: {
    identifier: string;
    iconUrl?: string;
    backgroundColor?: string;
    subtext?: string;
  };
}

const Placeholder = (props: PlaceholderProps<SearchResultOption, false>) => {
  return (
    <components.Placeholder
      // eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
      className={styles.searchPlaceholder}
      {...props}
    />
  );
};

const Control = (props: ControlProps<SearchResultOption, false>) => {
  const { isFocused } = props;
  return (
    <components.Control
      {...props}
      // eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
      className={classNames(styles.selectControl, {
        [styles.selectControlIsFocused]: isFocused,
      })}
    />
  );
};

const Input: typeof components.Input = (props) => (
  <components.Input
    {...props}
    // eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
    className={styles.searchInput}
  />
);

const Option = (props: OptionProps<SearchResultOption, false>) => {
  const { data, isFocused, isSelected } = props;
  const { identifier, iconUrl, backgroundColor, subtext } = data.value;
  const backgroundStyle: CSSProperties = {};

  let iconContent;

  if (identifier === SEE_ALL_RESULTS) {
    iconContent = <SearchIcon size="large" />;
  } else {
    if (iconUrl !== '') {
      backgroundStyle.backgroundImage = `url('${iconUrl}')`;
    }
    if (backgroundColor !== '') {
      backgroundStyle.backgroundColor = backgroundColor;
    }
    iconContent = (
      <div className={styles.searchResultIcon} style={backgroundStyle} />
    );
  }

  return (
    <components.Option
      {...props}
      // eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
      className={classNames({
        [styles.searchOptionIsFocused]: isFocused,
        [styles.searchOptionIsSelected]: isSelected,
      })}
    >
      <div className={styles.searchOption}>
        {iconContent}
        <div className={styles.searchResultDetails}>
          <div className={styles.searchResultName}>{data.label}</div>
          <div className={styles.searchResultOverview}>{subtext}</div>
        </div>
      </div>
    </components.Option>
  );
};

function onSelectHandler(
  { value }: SearchResultOption,
  query: string,
  resultUrlSlug: string,
  searchUrlSlug?: string,
) {
  if (value.identifier === SEE_ALL_RESULTS) {
    // Technically, we should never enter this block unless searchUrlSlug is non-null, but just
    // in case, we fall back to resultUrlSlug
    navigate(
      `/${searchUrlSlug ?? resultUrlSlug}/search?q=${encodeURIComponent(query)}`,
      {
        trigger: true,
      },
    );
  } else {
    navigate(`/${resultUrlSlug}/${value.identifier}`, { trigger: true });
  }
}

// Manually copied this type from atlassian-frontend repo since it's not exported from @atlaskit
interface FilterOption {
  readonly label: string;
  readonly value: string;
  readonly data: SearchResultOption;
}

export const SearchForm: FunctionComponent<{
  placeholder: string;
  large?: boolean;
  results: SearchResultOption[];
  setSearchValue: (val: string) => void;
  searchValue: string;
  // urlSlug for navigating to an individual search result
  resultUrlSlug: string;
  // Only used for when there is a dedicated search page that can be navigated to (power-ups only, not templates)
  searchUrlSlug?: string;
  // By default, this will filter the shown options to only ones where the search query appears in the label
  filterOption?:
    | ((option: FilterOption, inputValue: string) => boolean)
    | null
    | undefined;
}> = ({
  placeholder,
  large,
  results,
  setSearchValue,
  searchValue,
  searchUrlSlug,
  resultUrlSlug,
  filterOption,
}) => {
  // A non-null searchUrlSlug indicates that there is a search results page
  // that can be navigated to, so we display a "See all results" option
  if (searchUrlSlug && results.length > NUMBER_OF_RESULTS_TO_SHOW) {
    results = results.slice(0, NUMBER_OF_RESULTS_TO_SHOW);
    results.push({
      label: format('see-all-results-for', { query: searchValue }),
      value: {
        identifier: SEE_ALL_RESULTS,
        iconUrl: '',
        subtext: '',
      },
    });
  }

  const customStyles = {
    menu: (base: object) => ({
      ...base,
      backgroundColor: token('elevation.surface.overlay', '#FFFFFF'),
    }),
  };

  return (
    <Select
      // eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
      styles={customStyles}
      // eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
      className={classNames({
        [styles.large]: large,
      })}
      isSearchable
      filterOption={filterOption}
      menuIsOpen={searchValue.length > 0}
      placeholder={placeholder}
      options={results}
      value={null}
      noOptionsMessage={({ inputValue }: { inputValue: string }) =>
        format('no-results-for', { query: inputValue })
      }
      // @ts-expect-error

      onChange={(e: SearchResultOption) =>
        onSelectHandler(e, searchValue, resultUrlSlug, searchUrlSlug)
      }
      components={{
        Input,
        Control,
        Option,

        // eslint-disable-next-line @eslint-react/no-nested-component-definitions
        IndicatorsContainer: () => (
          <SearchIcon size={large ? 'large' : 'medium'} />
        ),
        Placeholder,
      }}
      onInputChange={(val: string) => setSearchValue(val)}
      large
    />
  );
};
